import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { BuilderPage, BuilderBlock, HeroBlockData, StoryBlockData, ApplicationsBlockData, CertificationsBlockData, CTABlockData, SpecsGridBlockData, GalleryBlockData, VideoBlockData } from "@/pages/admin/components/builder/types";
import { supabase } from "@/lib/supabase";

/** Sanitize HTML from RichTextEditor but preserve inline styles and image attributes */
const sanitizeRichHtml = (html: string): string =>
  DOMPurify.sanitize(html, {
    ADD_TAGS: ["iframe"],
    ALLOWED_ATTR: [
      "href", "src", "alt", "title", "target", "rel",
      "style", "class", "width", "height",
      "colspan", "rowspan", "allowfullscreen", "frameborder",
    ],
    FORCE_BODY: true,
  });

// ── Hero Block ────────────────────────────────────────────────────────────────
const HeroBlock: React.FC<{ data: HeroBlockData }> = ({ data }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ minHeight: 520, backgroundColor: "#0f172a" }}
    >
      {data.backgroundImage ? (
        <img
          src={data.backgroundImage}
          alt="hero"
          className="absolute inset-0 w-full h-full object-contain block"
          loading="eager"
          decoding="sync"
          style={{ display: "block", imageRendering: "-webkit-optimize-contrast" }}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#004A99] via-[#0066CC] to-[#00AEEF]" />
      )}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: `rgba(0,0,0,${Math.min(data.overlayOpacity / 100, 0.15)})` }}
      />
      <div
        className={`relative z-10 flex flex-col gap-5 px-8 md:px-20 py-24 md:py-32 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-95 translate-y-4"} ${
          data.textAlign === "center" ? "items-center text-center" : data.textAlign === "right" ? "items-end text-right" : "items-start text-left"
        }`}
      >
        {data.tagline && (
          <span className="text-xs md:text-sm font-bold tracking-[0.3em] text-white/70 uppercase border border-white/30 px-4 py-1.5 rounded-full backdrop-blur-sm">
            {data.tagline}
          </span>
        )}
        {data.title && (
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-tight max-w-4xl">
            {data.title}
          </h1>
        )}
        {data.subtitle && (
          <p className="text-base md:text-lg text-white/80 max-w-2xl leading-relaxed">
            {data.subtitle}
          </p>
        )}
        {data.ctaLabel && (
          <a
            href={data.ctaLink || "#contact"}
            className="mt-2 inline-flex items-center gap-2 px-8 py-3.5 bg-white text-[#004A99] text-sm font-bold rounded-full cursor-pointer whitespace-nowrap hover:bg-[#EDF2FB] transition-colors"
          >
            {data.ctaLabel}
            <i className="ri-arrow-right-line" />
          </a>
        )}
      </div>
    </div>
  );
};

// ── Story Item (extracted as component to allow hooks) ────────────────────────
const StoryItemCard: React.FC<{ item: StoryBlockData["items"][0] }> = ({ item }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const hasText = !!(item.title || item.content || item.accent);

  return (
    <div
      ref={ref}
      className={`flex flex-col gap-6 md:gap-8 items-center transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"} ${item.image && item.imagePosition === "right" && hasText ? "lg:flex-row-reverse" : item.image && hasText ? "lg:flex-row" : ""}`}
    >
      {item.image && (
        <div className={`flex-shrink-0 ${hasText ? "w-full lg:w-1/2" : "w-full"}`}>
          <div
            className="rounded-2xl overflow-hidden border-2 border-gray-300 shadow-md"
            style={{
              minHeight: 240,
              aspectRatio: hasText ? "4 / 3" : "16 / 9",
              backgroundColor: "#E8ECF3",
              backgroundImage: "radial-gradient(circle, #D1D8E0 1px, transparent 1px)",
              backgroundSize: "20px 20px",
            }}
          >
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-contain block p-3"
              loading="eager"
              decoding="sync"
              style={{
                display: "block",
                imageRendering: "-webkit-optimize-contrast",
                maxHeight: "100%",
              }}
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
            />
          </div>
        </div>
      )}
      <div className={`space-y-5 ${item.image && hasText ? "w-full lg:w-1/2" : "w-full max-w-3xl mx-auto"}`}>
        {item.accent && (
          <span className="inline-block text-xs font-bold tracking-[0.2em] text-[#004A99] uppercase bg-[#EDF2FB] px-4 py-1.5 rounded-full">
            {item.accent}
          </span>
        )}
        {item.title && (
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
            {item.title}
          </h2>
        )}
        <div
          className="text-gray-600 leading-relaxed w-full overflow-hidden
            [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mb-3
            [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mb-2
            [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mb-2
            [&_p]:mb-2.5 [&_p]:leading-relaxed [&_p]:text-sm
            [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:mb-2 [&_ul>li]:mb-1 [&_ul>li]:text-sm
            [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:mb-2 [&_ol>li]:mb-1 [&_ol>li]:text-sm
            [&_strong]:font-bold [&_strong]:text-gray-900
            [&_em]:italic
            [&_a]:text-[#004A99] [&_a]:underline
            [&_blockquote]:border-l-4 [&_blockquote]:border-[#004A99] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-500 [&_blockquote]:my-3
            [&_img]:rounded-xl [&_img]:my-3 [&_img]:block [&_img]:max-w-full [&_img]:h-auto [&_img]:border [&_img]:border-gray-200 [&_img]:shadow-sm"
          style={{ maxWidth: '100%' }}
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(item.content) }}
        />
      </div>
    </div>
  );
};

// ── Story Block ───────────────────────────────────────────────────────────────
const StoryBlock: React.FC<{ data: StoryBlockData }> = ({ data }) => {
  if (!data.items || data.items.length === 0) return null;
  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-3 md:px-6 space-y-16 md:space-y-24">
        {data.items.map((item) => (
          <StoryItemCard key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
};

// ── Applications Block ────────────────────────────────────────────────────────
const ApplicationsBlock: React.FC<{ data: ApplicationsBlockData }> = ({ data }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  if (!data.items || data.items.length === 0) return null;

  return (
    <section className="py-12 md:py-20 bg-[#F8FAFE]">
      <div className="max-w-[1200px] mx-auto px-3 md:px-6">
        <div ref={ref} className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-[0.25em] text-[#004A99] uppercase mb-3">Ứng Dụng</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              {data.title || "Ứng dụng thực tế"}
            </h2>
            {data.subtitle && <p className="text-gray-500 mt-3 max-w-2xl mx-auto">{data.subtitle}</p>}
          </div>
          <div className={`grid gap-6 ${data.layout === "masonry" ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
            {data.items.map((item, i) => (
              <div
                key={item.id}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#004A99]/30 transition-all"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                {item.image_url && (
                  <div
                    className="overflow-hidden rounded-2xl border-2 border-gray-300 shadow-md"
                    style={{
                      minHeight: 180,
                      aspectRatio: "16 / 9",
                      backgroundColor: "#E8ECF3",
                      backgroundImage: "radial-gradient(circle, #D1D8E0 1px, transparent 1px)",
                      backgroundSize: "20px 20px",
                    }}
                  >
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-contain block p-2"
                      loading="eager"
                      decoding="sync"
                      style={{
                        display: "block",
                        imageRendering: "-webkit-optimize-contrast",
                        maxHeight: "100%",
                      }}
                    />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  {item.description && (
                    <div
                      className="text-sm text-gray-500 line-clamp-3 w-full overflow-hidden [&_img]:max-w-full [&_img]:block"
                      dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(item.description) }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Certifications Block ──────────────────────────────────────────────────────
const CertificationsBlock: React.FC<{ data: CertificationsBlockData }> = ({ data }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  if (!data.items || data.items.length === 0) return null;

  const typeLabel: Record<string, string> = {
    certification: "Chứng nhận",
    patent: "Bằng sáng chế",
    award: "Giải thưởng",
  };

  const typeColor: Record<string, string> = {
    certification: "bg-emerald-50 text-emerald-700",
    patent: "bg-[#EDF2FB] text-[#004A99]",
    award: "bg-amber-50 text-amber-700",
  };

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-3 md:px-6">
        <div ref={ref} className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-[0.25em] text-[#004A99] uppercase mb-3">Tin Tưởng</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              {data.title || "Bằng sáng chế & Chứng nhận"}
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {data.items.map((item, i) => (
              <div
                key={item.id}
                className="flex flex-col items-center text-center p-6 rounded-2xl border border-gray-100 hover:border-[#004A99]/20 transition-all"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                {item.image_url && (
                  <img src={item.image_url} alt={item.title} className="w-20 h-20 object-contain mb-4" loading="lazy" />
                )}
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full mb-2 ${typeColor[item.type] ?? "bg-gray-100 text-gray-600"}`}>
                  {typeLabel[item.type] ?? item.type}
                </span>
                <h4 className="font-bold text-gray-800 text-sm leading-snug">{item.title}</h4>
                {item.description && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{item.description}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ── CTA Block ─────────────────────────────────────────────────────────────────
const CTABlock: React.FC<{ data: CTABlockData; productName?: string }> = ({ data, productName }) => {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await supabase.from("inquiries").insert({
        product_name: productName ?? "",
        customer_name: formData["Họ và tên"] ?? formData["name"] ?? "",
        email: formData["Email"] ?? formData["email"] ?? "",
        phone: formData["Số điện thoại"] ?? formData["phone"] ?? "",
        message: formData["Nội dung yêu cầu"] ?? formData["message"] ?? "",
        status: "new",
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20" style={{ background: data.backgroundColor, color: data.textColor }}>
      <div className="max-w-[900px] mx-auto px-8 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-3">
          {data.title}
        </h2>
        {data.subtitle && <p className="text-base opacity-80 mb-10">{data.subtitle}</p>}

        {data.showForm && !submitted && (
          <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-left space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {data.formFields.map((field, i) => (
                <div key={i} className={field.type === "textarea" ? "sm:col-span-2" : ""}>
                  <label className="block text-xs font-semibold mb-1.5 opacity-80">
                    {field.label}{field.required && " *"}
                  </label>
                  {field.type === "textarea" ? (
                    <textarea
                      required={field.required}
                      rows={4}
                      value={formData[field.label] ?? ""}
                      onChange={(e) => setFormData((prev) => ({ ...prev, [field.label]: e.target.value }))}
                      className="w-full bg-white/20 border border-white/30 rounded-xl px-4 py-3 text-sm outline-none focus:border-white/60 resize-none placeholder-white/40"
                      style={{ color: data.textColor }}
                      placeholder={field.label}
                    />
                  ) : (
                    <input
                      type={field.type}
                      required={field.required}
                      value={formData[field.label] ?? ""}
                      onChange={(e) => setFormData((prev) => ({ ...prev, [field.label]: e.target.value }))}
                      className="w-full bg-white/20 border border-white/30 rounded-xl px-4 py-3 text-sm outline-none focus:border-white/60 placeholder-white/40"
                      style={{ color: data.textColor }}
                      placeholder={field.label}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="text-center pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-10 py-3.5 rounded-full text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-60 transition-opacity"
                style={{ background: data.buttonColor, color: "#fff" }}
              >
                {submitting && <i className="ri-loader-4-line animate-spin" />}
                {data.buttonLabel}
              </button>
            </div>
          </form>
        )}

        {submitted && (
          <div className="bg-white/20 rounded-2xl p-10 text-center">
            <i className="ri-checkbox-circle-line text-5xl block mb-4 opacity-90" />
            <p className="text-xl font-bold">Đã gửi thành công!</p>
            <p className="text-sm opacity-70 mt-2">Chúng tôi sẽ liên hệ với bạn trong thời gian sớm nhất.</p>
          </div>
        )}

        {!data.showForm && (
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-10 py-4 rounded-full text-sm font-bold cursor-pointer whitespace-nowrap mt-4"
            style={{ background: data.buttonColor, color: "#fff" }}
          >
            {data.buttonLabel}
            <i className="ri-arrow-right-line" />
          </a>
        )}
      </div>
    </section>
  );
};

// ── Specs Grid Block ──────────────────────────────────────────────────────────
const SpecsGridBlock: React.FC<{ data: SpecsGridBlockData }> = ({ data }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  if (!data.items || data.items.length === 0) return null;

  const colClass: Record<number, string> = { 2: "grid-cols-2", 3: "grid-cols-2 md:grid-cols-3", 4: "grid-cols-2 md:grid-cols-4" };

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-3 md:px-6">
        <div ref={ref} className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-[0.25em] text-[#004A99] uppercase mb-3">Thông Số</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              {data.title || "Thông số kỹ thuật"}
            </h2>
          </div>
          <div className={`grid gap-4 ${colClass[data.columns] ?? "grid-cols-3"}`}>
            {data.items.map((item, i) => (
              <div
                key={i}
                className="bg-[#F8FAFE] rounded-2xl p-5 border border-[#004A99]/10 hover:border-[#004A99]/30 transition-colors"
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                <p className="text-[10px] font-bold tracking-wider text-[#004A99]/60 uppercase mb-1">{item.label}</p>
                <p className="text-lg font-bold text-gray-900">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Gallery Block ─────────────────────────────────────────────────────────────
const GalleryBlock: React.FC<{ data: GalleryBlockData }> = ({ data }) => {
  const [visible, setVisible] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  if (!data.images || data.images.length === 0) return null;

  const colClass: Record<string, string> = {
    "2col": "grid-cols-1 sm:grid-cols-2",
    "3col": "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    masonry: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    staggered: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  };

  return (
    <section className="py-12 md:py-20 bg-[#F8FAFE]">
      <div className="max-w-[1200px] mx-auto px-3 md:px-6">
        <div ref={ref} className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-[0.25em] text-[#004A99] uppercase mb-3">Hình Ảnh</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              {data.title || "Thư viện hình ảnh"}
            </h2>
          </div>
          <div className={`grid gap-4 ${colClass[data.layout] ?? "grid-cols-3"}`}>
            {data.images.map((img, i) => (
              <div
                key={i}
                className="group relative overflow-hidden rounded-2xl cursor-pointer"
                style={{ aspectRatio: data.layout === "staggered" && i % 3 === 0 ? "16/9" : "4/3" }}
                onClick={() => setLightbox(img.url)}
              >
                <img
                  src={img.url}
                  alt={img.caption}
                  className="w-full h-full object-contain block p-2"
                  loading="eager"
                  decoding="sync"
                  style={{
                    display: "block",
                    imageRendering: "-webkit-optimize-contrast",
                    maxHeight: "100%",
                  }}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <i className="ri-zoom-in-line text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {img.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4 translate-y-full group-hover:translate-y-0 transition-transform">
                    <p className="text-white text-sm font-medium">{img.caption}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain rounded-xl" />
          <button className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/20 rounded-full text-white cursor-pointer hover:bg-white/30">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
      )}
    </section>
  );
};

// ── Video Block ──────────────────────────────────────────────────────────────
const VideoBlock: React.FC<{ data: VideoBlockData }> = ({ data }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  if (!data.url) return null;

  // Detect if URL is a YouTube or Vimeo embed link
  const isYoutube = data.url.includes("youtube.com") || data.url.includes("youtu.be");
  const isVimeo = data.url.includes("vimeo.com");

  const getEmbedUrl = (url: string) => {
    // YouTube: convert watch URL to embed
    if (url.includes("youtube.com/watch")) {
      const videoId = new URL(url).searchParams.get("v");
      return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0` : url;
    }
    if (url.includes("youtu.be/")) {
      const videoId = url.split("youtu.be/")[1]?.split("?")[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0` : url;
    }
    // Vimeo: convert to embed
    if (url.includes("vimeo.com/") && !url.includes("/video/")) {
      const videoId = url.split("vimeo.com/")[1]?.split("?")[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url;
    }
    return url;
  };

  const embedUrl = (isYoutube || isVimeo) ? getEmbedUrl(data.url) : null;

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-3 md:px-6">
        <div ref={ref} className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="rounded-2xl overflow-hidden border border-gray-100" style={{ aspectRatio: "16/9", background: "#000", position: "relative" }}>
            {embedUrl ? (
              <iframe
                src={embedUrl}
                title={data.caption || "Video"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full absolute inset-0"
                style={{ border: "none", display: "block" }}
              />
            ) : (
              <video
                src={data.url}
                poster={data.poster || undefined}
                controls={data.controls !== false}
                autoPlay={data.autoplay === true}
                loop={data.loop === true}
                muted={data.muted === true}
                playsInline
                className="w-full h-full object-contain bg-black block absolute inset-0"
                style={{ maxHeight: "100%" }}
              />
            )}
          </div>
          {data.caption && (
            <p className="text-center text-sm text-gray-400 mt-3">{data.caption}</p>
          )}
        </div>
      </div>
    </section>
  );
};

// ── Single Block Renderer ─────────────────────────────────────────────────────
const BlockRenderer: React.FC<{ block: BuilderBlock; productName?: string }> = ({ block, productName }) => {
  switch (block.data.type) {
    case "hero": return <HeroBlock data={block.data as HeroBlockData} />;
    case "story": return <StoryBlock data={block.data as StoryBlockData} />;
    case "applications": return <ApplicationsBlock data={block.data as ApplicationsBlockData} />;
    case "certifications": return <CertificationsBlock data={block.data as CertificationsBlockData} />;
    case "cta": return <CTABlock data={block.data as CTABlockData} productName={productName} />;
    case "specs_grid": return <SpecsGridBlock data={block.data as SpecsGridBlockData} />;
    case "gallery": return <GalleryBlock data={block.data as GalleryBlockData} />;
    case "video": return <VideoBlock data={block.data as VideoBlockData} />;
    default: return null;
  }
};

// ── Main BuilderRenderer ──────────────────────────────────────────────────────
interface BuilderRendererProps {
  pages: BuilderPage[];
  productName?: string;
}

const BuilderRenderer: React.FC<BuilderRendererProps> = ({ pages, productName }) => {
  const [activePage, setActivePage] = useState(0);

  // eslint-disable-next-line no-console
  console.debug("[BuilderRenderer] pages.length=", pages?.length, "| productName=", productName);

  if (!pages || pages.length === 0) return null;

  const currentPage = pages[activePage] ?? pages[0];
  const hasBlocks = currentPage.blocks && currentPage.blocks.length > 0;

  // eslint-disable-next-line no-console
  console.debug("[BuilderRenderer] currentPage.blocks.length=", currentPage.blocks?.length, "| hasBlocks=", hasBlocks);

  if (!hasBlocks && pages.length === 1) return null;

  return (
    <div>
      {/* Multi-page tabs */}
      {pages.length > 1 && (
        <div className="sticky top-16 z-30 bg-white border-b border-gray-100">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="flex items-center gap-1 overflow-x-auto py-3">
              {pages.map((page, idx) => (
                <button
                  key={page.id}
                  onClick={() => { setActivePage(idx); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                    activePage === idx
                      ? "bg-[#004A99] text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <i className="ri-file-text-line text-xs" />
                  {page.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Render blocks */}
      {currentPage.blocks.map((block) => (
        <BlockRenderer key={block.id} block={block} productName={productName} />
      ))}
    </div>
  );
};

export default BuilderRenderer;
